/**
 * New-version prompt (shared by all GridX apps, cache-busting ticket). Each build writes /version.json (no-store) and bakes the same
 * id into the bundle as __APP_VERSION__. This module compares them on open, when the tab becomes visible again, when the browser
 * comes back online and every few minutes, and shows a small "New version ready - Update" banner. It NEVER reloads by itself, so
 * nobody loses work. "Update" deletes Cache Storage and unregisters service workers (never localStorage, sessionStorage or
 * cookies, so nobody is signed out) and reloads. Plain DOM on purpose: it must also run on strict-CSP pages and before the app mounts.
 */
declare const __APP_VERSION__: string | undefined;

const CHECK_MS = 5 * 60_000;

type Copy = { title: string; update: string; later: string; updating: string };
const COPY: Record<string, Copy> = {
  en: { title: 'New version ready', update: 'Update', later: 'Later', updating: 'Updating...' },
  ur: { title: 'نیا ورژن تیار ہے', update: 'اپڈیٹ کریں', later: 'بعد میں', updating: 'اپڈیٹ ہو رہا ہے...' },
  ar: { title: 'إصدار جديد جاهز', update: 'تحديث', later: 'لاحقاً', updating: 'جارٍ التحديث...' },
  ro: { title: 'Naya version tayyar hai', update: 'Update karein', later: 'Baad mein', updating: 'Update ho raha hai...' },
};

export function pickCopy(lang: string | null | undefined): Copy {
  const base = (lang ?? '').toLowerCase().split(/[-_]/);
  // Roman Urdu is stored as "ro" or as "ur-Latn" depending on the product
  if (base[0] === 'ur' && base[1] === 'latn') return COPY.ro;
  return COPY[base[0]] ?? COPY.en;
}

/** True when the server's version differs from the bundle's own. Unknown or missing ids never count as a new version. */
export function isNewVersion(running: string | null | undefined, served: unknown): boolean {
  return typeof served === 'string' && served.length > 0 && !!running && served !== running;
}

/** Deletes Cache Storage and unregisters service workers. Never touches localStorage, sessionStorage or cookies. */
export async function clearAppCaches(): Promise<void> {
  try {
    const regs = (await navigator.serviceWorker?.getRegistrations?.()) ?? [];
    await Promise.all(regs.map((r) => r.unregister()));
    if (typeof caches !== 'undefined') await Promise.all((await caches.keys()).map((k) => caches.delete(k)));
  } catch {
    /* reload anyway */
  }
}

let started = false;

export function startVersionWatch(): void {
  const running = typeof __APP_VERSION__ === 'string' ? __APP_VERSION__ : '';
  if (started || typeof window === 'undefined' || !running || (import.meta as { env?: { DEV?: boolean } }).env?.DEV) return;
  started = true;
  let banner: HTMLElement | null = null;
  let busy = false;

  const hide = () => {
    banner?.remove();
    banner = null;
  };

  const show = () => {
    if (banner) return;
    const t = pickCopy(document.documentElement.lang);
    const el = document.createElement('div');
    el.setAttribute('role', 'status');
    el.setAttribute('aria-live', 'polite');
    el.setAttribute('data-app-update-banner', '');
    el.dir = document.documentElement.dir === 'rtl' ? 'rtl' : 'ltr';
    el.style.cssText =
      'position:fixed;left:16px;right:16px;bottom:16px;margin:0 auto;max-width:560px;z-index:2147483000;display:flex;flex-wrap:wrap;' +
      'align-items:center;gap:8px 12px;padding:12px 14px;border-radius:12px;background:#0f172a;color:#f8fafc;' +
      'border:1px solid #334155;box-shadow:0 10px 30px rgba(0,0,0,.35);font:500 14px/1.4 system-ui,-apple-system,"Segoe UI",Roboto,sans-serif';
    const text = document.createElement('span');
    text.style.cssText = 'flex:1 1 160px';
    text.textContent = t.title;
    const update = document.createElement('button');
    update.type = 'button';
    update.textContent = t.update;
    update.style.cssText =
      'min-height:44px;padding:0 14px;border:0;border-radius:8px;background:#6366f1;color:#fff;font:inherit;font-weight:600;cursor:pointer';
    const later = document.createElement('button');
    later.type = 'button';
    later.textContent = t.later;
    later.style.cssText = 'min-height:44px;padding:0 10px;border:0;background:transparent;color:#cbd5e1;font:inherit;cursor:pointer';
    update.addEventListener('click', () => {
      if (busy) return;
      busy = true;
      update.disabled = true;
      update.textContent = t.updating;
      void clearAppCaches().then(() => window.location.reload());
    });
    later.addEventListener('click', hide);
    const style = document.createElement('style');
    style.textContent = '@media print{[data-app-update-banner]{display:none!important}}';
    el.append(text, update, later, style);
    document.body.appendChild(el);
    banner = el;
  };

  const check = async () => {
    try {
      const res = await fetch('/version.json?t=' + Date.now(), { cache: 'no-store' });
      if (!res.ok) return;
      const body = (await res.json()) as { version?: unknown };
      if (isNewVersion(running, body.version)) show();
      else hide();
    } catch {
      /* offline or an old deploy without version.json: stay silent */
    }
  };

  void check();
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') void check();
  });
  window.addEventListener('online', () => void check());
  window.setInterval(() => void check(), CHECK_MS);
}
